/**
 * WritingStylesManager Component
 *
 * T077: Lazy load WritingStyleAnalysis for performance
 */

import { useState, useCallback, Suspense, lazy } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { Button } from '@/components/ui/button';
import { Plus, Loader2 } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { IWritingStyleSummary, IWritingStyleAnalysis } from '@/@types/writingStyle';
import WritingStyleCard from './WritingStyleCard';
import WritingStyleForm from './WritingStyleForm';

// T077: Lazy load WritingStyleAnalysis for better initial bundle size
const WritingStyleAnalysis = lazy(() => import('./WritingStyleAnalysis'));
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const WritingStylesManager = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<Id<'writingStyles'> | null>(null);
  // T066: State for analysis modal
  const [analysisStyleId, setAnalysisStyleId] = useState<Id<'writingStyles'> | null>(null);

  const styles = useQuery(api.writingStyles.getMyWritingStyles);
  const deleteStyle = useMutation(api.writingStyles.deleteWritingStyle);
  // T066: Query for the selected style's full data (including analysis)
  const selectedStyleData = useQuery(
    api.writingStyles.getWritingStyle,
    analysisStyleId ? { styleId: analysisStyleId } : 'skip'
  );

  const handleCreateClick = useCallback(() => {
    setShowCreateForm(true);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  const handleFormCancel = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  const handleDeleteClick = useCallback((styleId: Id<'writingStyles'>) => {
    setDeleteConfirmId(styleId);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteStyle({ styleId: deleteConfirmId });
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete writing style:', error);
    }
  }, [deleteConfirmId, deleteStyle]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  // T066: Handler for viewing analysis
  const handleViewAnalysis = useCallback((styleId: Id<'writingStyles'>) => {
    setAnalysisStyleId(styleId);
  }, []);

  const handleCloseAnalysis = useCallback(() => {
    setAnalysisStyleId(null);
  }, []);

  if (styles === undefined) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Writing Styles</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage writing style profiles for consistent content generation
          </p>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          Create Style
        </Button>
      </div>

      {/* Empty State */}
      {styles.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            <h3 className="text-xl font-semibold">No Writing Styles Yet</h3>
            <p className="text-muted-foreground">
              Create your first writing style by analyzing a text sample. The AI will extract
              tone, structure, vocabulary, and rhetorical patterns to help you generate
              content that matches your unique voice.
            </p>
            <Button onClick={handleCreateClick} size="lg">
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Style
            </Button>
          </div>
        </div>
      )}

      {/* Styles Grid */}
      {styles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {styles.map((style: IWritingStyleSummary) => (
            <WritingStyleCard
              key={style._id}
              style={style}
              onDelete={handleDeleteClick}
              onViewAnalysis={handleViewAnalysis}
            />
          ))}
        </div>
      )}

      {/* Create Form Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Writing Style</DialogTitle>
            <DialogDescription>
              Analyze a text sample to create a reusable writing style profile
            </DialogDescription>
          </DialogHeader>
          <WritingStyleForm onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={handleDeleteCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Writing Style?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this writing style. Any topics using this style
              will revert to the default writing style. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* T066: Analysis View Dialog with T077 Lazy Loading */}
      <Dialog open={analysisStyleId !== null} onOpenChange={handleCloseAnalysis}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedStyleData?.analysis ? (
            <Suspense
              fallback={
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <WritingStyleAnalysis
                analysis={selectedStyleData.analysis as IWritingStyleAnalysis}
                onClose={handleCloseAnalysis}
              />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WritingStylesManager;
